import { Injectable } from '@angular/core';
import { ApiService } from '../../core/api.service';
import { SyncCryptService } from '../../core/crypt/sync-crypt.service';
import { LoggerService } from '../../core/logger.service';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../reducers';
import { Subscription } from 'rxjs';
import { DownloadZip, ErrCode, FileItem } from '../../shared/models';
import { first } from 'rxjs/operators';
import { TransferService } from './transfer.service';
import { TransferStatus } from '../models/transfer.model';
import { ExternalNavigationHandlerService } from "./external-navigation-handler.service";

@Injectable({
    providedIn: 'root'
})
export class DownloadZipService {

    private jobSub: Subscription;
    public downloadZip: { [key: string]: DownloadZip } = {};

    constructor(
        private api: ApiService,
        private crypt: SyncCryptService,
        private log: LoggerService,
        private store: Store<fromRoot.State>,
        private transferService: TransferService,
        private externalNavigationHandlerService: ExternalNavigationHandlerService
    ) {
        this.externalNavigationHandlerService.addHandler(this.navigationHandler);
    }

    /**
     * Creates a zip and provides the result callback.
     */
    public createZip(linkID: string, linkPassword: string, zipName: string): void {
        const jobId = this.crypt.getRandomHex(32);
        this.downloadZip[jobId] = {
            name: zipName,
            dlReady: false,
            created: Date.now(),
            status: TransferStatus.STATUS_DOWNLOAD,
            retryCount: 0
        };
        this.transferService.showTransfer();

        this.store.select(fromRoot.getLinkFileListState)
            .pipe(first())
            .subscribe((data) => {
                if (data.cwd) {
                    this.downloadZip[jobId].pid = data.cwd.sync_id;
                }
                const files = data.pathlist.filter(val => val.sync_id && val.share_key);
                this.downloadZip[jobId].sharekey = data.cwd.share_key;
                this.processZipCreation(linkID, linkPassword, files, jobId);
            });
    }

    /**
     * Creates a zip and provides the result callback.
     */
    public retry(linkID: string, linkPassword: string, zipName: string, downloadZipItem: DownloadZip): void {
        const jobId = downloadZipItem.jobId;
        this.downloadZip[jobId] = { ...downloadZipItem };
        this.downloadZip[jobId].errcode = undefined;
        this.downloadZip[jobId].dlReady = false;
        this.downloadZip[jobId].status = TransferStatus.STATUS_DOWNLOAD;
        this.downloadZip[jobId].retryCount = this.downloadZip[jobId].retryCount + 1;
        this.transferService.showTransfer();

        this.store.select(fromRoot.getLinkFileListState)
            .pipe(first())
            .subscribe((data) => {
                if (data.cwd) {
                    this.downloadZip[jobId].pid = data.cwd.sync_id;
                }
                const files = data.pathlist.filter(val => val.sync_id && val.share_key);
                this.downloadZip[jobId].sharekey = data.cwd.share_key;
                this.processZipCreation(linkID, linkPassword, files, jobId);
            });
    }

    /**
     * Process the zip creation job.
     * @param linkID The public link ID.
     * @param linkPassword The password for the link.
     * @param files The files to be included in the zip.
     */
    private async processZipCreation(linkID: string, linkPassword: string, files: FileItem[], jobId: string): Promise<void> {
        const itemlist: number[] = [];
        const cachekeylist: string[] = [];

        files.forEach(file => {
            itemlist.push(file.sync_id);
            cachekeylist.push(file.cachekey != null ? file.cachekey + file.sync_id : file.sync_id.toString());
        });
        try {
            // Call API to initiate the zip job.
            await this.api.execute('setjob', {
                publink_id: linkID,
                sync_id_list: itemlist,
                pid: this.downloadZip[jobId].pid,
                job_name: 'zipreport.Zipreport',
                share_key: this.downloadZip[jobId].sharekey,
                job_id: jobId,
                password: linkPassword,
                cachekeylist: cachekeylist
            });

            // Subscribe to the job status.
            this.jobSub = this.api.subscribeJob(jobId).subscribe((data) => {
                this.handleJobStatus(data, jobId, linkID);
            });
        } catch (ex) {
            this.downloadZip[jobId].errcode = ErrCode.fromException(ex);
            this.downloadZip[jobId].status = TransferStatus.STATUS_ERROR;
            this.log.e('Error occurred zipping file', this.downloadZip[jobId].errcode);
        }
    }

    /**
     * Handles the response of the zip creation job.
     * @param data The job status data.
     * @param jobId The job ID.
     * @param linkID The public link ID.
     */
    private handleJobStatus(data: any, jobId: string, linkID: string): void {
        if (data['status'] && data['status'] === 'error') {
            this.handleError(data, jobId);
        } else if (data['status'] === 'success') {
            this.handleSuccess(data, jobId);
        } else if (data['status'] === 'fail') {
            this.handleFail(jobId);
        } else {
            this.handleUnknownError(jobId);
        }
    }

    /**
     * Handles the error status of the zip creation job.
     * @param data The error job status data.
     * @param jobId The job ID.
     */
    private handleError(data: any, jobId: string): void {
        if (data['downloading'].length) {
            const errormsg = data['downloading'][0];
            const regex = /\((\d+)\)/;
            const found = errormsg.match(regex);
            if (found != null && found.length === 2) {
                this.downloadZip[jobId].errcode = new ErrCode(parseInt('15' + found[1], 10));
            } else {
                this.downloadZip[jobId].errcode = new ErrCode(7500);
            }
            this.log.error('ZIP error: ' + errormsg);
        }
        this.log.e('Error received in link job subscription', this.downloadZip[jobId].errcode);
        this.downloadZip[jobId].dlReady = false;
        this.downloadZip[jobId].status = TransferStatus.STATUS_ERROR;
    }

    /**
     * Handles the success status of the zip creation job.
     * @param data The success job status data.
     * @param jobId The job ID.
     */
    private handleSuccess(data: any, jobId: string): void {
        if (data['totalsize'] > 0) {
            this.downloadZip[jobId].itemsize = data['totalsize'];
            this.downloadZip[jobId].zipUrl = [
                data['url'],
                '&pid=', data['jsonname'],
                '&jid=', jobId
            ].join('');
            this.downloadZip[jobId].dlReady = true;
            this.downloadZip[jobId].status = TransferStatus.STATUS_SUCCESS;

            // automatically downloading the file after zip got created.
            let link = document.createElement('a');
            link.href = this.downloadZip[jobId].zipUrl;
            link.download = this.downloadZip[jobId].name;
            link.click();

            this.log.info('Zip is ready to download');
            this.api.send('linkconsume', {
                links: [{ publink_id: data['publink_id'] }]
            });
        } else {
            this.downloadZip[jobId].errcode = new ErrCode(7501);
            this.downloadZip[jobId].status = TransferStatus.STATUS_ERROR;
            this.log.e('Total size of zip job was 0', this.downloadZip[jobId].errcode);
        }
    }

    /**
     * Handles the fail status of the zip creation job.
     * @param jobId The job ID.
     */
    private handleFail(jobId: string): void {
        this.downloadZip[jobId].errcode = new ErrCode(7502);
        this.downloadZip[jobId].status = TransferStatus.STATUS_ERROR;
        this.log.e('Server error: not enough space', this.downloadZip[jobId].errcode);
        this.downloadZip[jobId].dlReady = false;
    }

    /**
     * Handles an unknown error status of the zip creation job.
     * @param jobId The job ID.
     */
    private handleUnknownError(jobId: string): void {
        this.downloadZip[jobId].errcode = new ErrCode(7503);
        this.log.e('Server error: Unknown status code', this.downloadZip[jobId].errcode);
        this.downloadZip[jobId].dlReady = false;
        this.downloadZip[jobId].status = TransferStatus.STATUS_ERROR;
    }

    public removeItem(jonId: string): void {
        this.downloadZip
        let { [jonId]: omit, ...res } = this.downloadZip;
        this.downloadZip = res;
    }

    public reset(): void {
        this.downloadZip = {};
    }

    public isProcessing(): boolean {
        return Object.values(this.downloadZip).some(item => !item.dlReady && !item.errcode);
    }

    /**
     * Unsubscribe from the job subscription if it's active.
     */
    public unsubscribeJob(): void {
        if (this.jobSub) {
            this.jobSub.unsubscribe();
        }
    }

    private navigationHandler = () => {
        if (this.isProcessing()) {
            return 'There is unfinished upload/download pending.';
        }
        return undefined;
    }

}
